// The canvas of the schedule editor (R-S6, decision 036): rooms or tracks as columns, a time scale of the event's working hours;
// talks, palette blocks and slots are dropped on it, a slot's bottom edge stretches its length.

import { useRef, useState } from 'react';
import type { Id, RecordObj } from '@confdeck/core-shared';
import { cx, Icon } from '@confdeck/core-web';
import { SLOT_ICONS } from '../src/schedule-build.ts';
import { hhmm, toMin } from '../src/schedule-time.ts';
import { columnOf, gridColumns } from './schedule-columns.ts';
import s from './schedule.module.css';

/** 15 minutes take one row of 22 px; the step only decides where a dropped slot snaps (Р-29). */
export const PX = 22 / 15;
const ROW = 22;

export type Drag = { kind: 'talk'; id: Id } | { kind: 'slot'; id: Id } | { kind: 'block'; block: string };
export interface Cell { col: Id; min: number }

export function ScheduleGrid({ columns: terms, slots, axis, step, from, to, drag, setDrag, conflicts, selected, onSelect, onDrop, onResize, onAddBreak, hasTalk }: {
  columns: { id: Id; name: string }[]; slots: RecordObj[]; axis: 'room' | 'track'; step: number; from: number; to: number;
  drag: Drag | null; setDrag: (d: Drag | null) => void; conflicts: Map<Id, string>; selected: Id | null; onSelect: (id: Id | null) => void;
  onDrop: (cell: Cell, d: Drag) => void; onResize: (slot: RecordObj, duration: number) => void; onAddBreak: (cell: Cell) => void; hasTalk: (r: RecordObj) => boolean;
}) {
  // A slot with no room (or track) of the grid — none set, or its value removed — stands in an extra column, marked (P2-10).
  const columns = gridColumns(terms, slots.map((sl) => sl.data[axis]), axis);
  const gridRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<Cell | null>(null);
  const [stretch, setStretch] = useState<{ id: Id; duration: number } | null>(null);

  const cellAt = (x: number, y: number): Cell | null => {
    const g = gridRef.current;
    if (!g || !columns.length) return null;
    const r = g.getBoundingClientRect();
    const col = Math.floor(((x - r.left) / r.width) * columns.length);
    const min = from + Math.floor((y - r.top) / PX / step) * step;
    if (col < 0 || col >= columns.length || columns[col]!.orphan || min < from || min >= to) return null;
    return { col: columns[col]!.id, min };
  };

  // Stretching the bottom edge: the length follows the pointer by the step; the slots after it follow on release.
  const startStretch = (e: React.PointerEvent, sl: RecordObj) => {
    e.preventDefault(); e.stopPropagation();
    const top = e.clientY;
    const base = Number(sl.data.duration ?? 15);
    let cur = base;
    const move = (ev: PointerEvent) => { cur = Math.max(step, base + Math.round((ev.clientY - top) / PX / step) * step); setStretch({ id: sl.id, duration: cur }); };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); setStretch(null); if (cur !== base) onResize(sl, cur); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const hours = Array.from({ length: Math.ceil((to - from) / 60) }, (_, i) => from + i * 60);
  const template = `repeat(${columns.length}, minmax(140px, 1fr))`;
  return (
    <div className={s.canvas}>
      <div className={s.canvasInner} style={{ minWidth: 56 + columns.length * 140 }}>
        <div className={s.colsHead} style={{ gridTemplateColumns: `56px ${template}` }}>
          <span />{columns.map((c) => <span key={c.id} className={cx(s.colName, c.orphan && s.colOrphan)} title={c.orphan ? 'Слоты без зала или трека этой сетки: выберите его в параметрах слота' : c.name}>{c.name}</span>)}
        </div>
        <div className={s.body} style={{ gridTemplateColumns: '56px 1fr' }}>
          <div>{hours.map((h) => <div key={h} className={s.hour} style={{ height: 60 * PX }}>{hhmm(h)}</div>)}</div>
          <div ref={gridRef} className={s.grid} style={{ height: (to - from) * PX, gridTemplateColumns: template }}
            onDragOver={(e) => { e.preventDefault(); setHover(cellAt(e.clientX, e.clientY)); }} onDragLeave={() => setHover(null)}
            onDrop={(e) => { e.preventDefault(); const c = cellAt(e.clientX, e.clientY); setHover(null); const d = drag; setDrag(null); if (c && d) onDrop(c, d); }}
            onClick={(e) => { if (!(e.target as HTMLElement).closest('[data-slot]')) onSelect(null); }}
            onDoubleClick={(e) => { if ((e.target as HTMLElement).closest('[data-slot]')) return; const c = cellAt(e.clientX, e.clientY); if (c) onAddBreak(c); }}>
            {columns.map((c, i) => <div key={c.id} className={cx(s.col, c.orphan && s.colOrphanBody)} style={{ gridColumn: `${i + 1} / span 1` }} />)}
            {hover && <div className={s.dropHint} style={{ gridColumn: `${columns.findIndex((c) => c.id === hover.col) + 1} / span 1`, top: (hover.min - from) * PX, height: ROW * 2 }}><span>{hhmm(hover.min)}</span></div>}
            {slots.map((sl) => {
              const col = columnOf(columns, sl.data[axis]);
              const m = toMin(sl.data.start);
              if (col < 0 || Number.isNaN(m)) return null;
              const duration = stretch?.id === sl.id ? stretch.duration : Number(sl.data.duration ?? 15);
              const bad = conflicts.get(sl.id) ?? (columns[col]!.orphan && sl.data[axis] ? (axis === 'room' ? 'Зал удалён — выберите другой' : 'Трек удалён — выберите другой') : undefined);
              const color = typeof sl.data.color === 'string' ? sl.data.color : null;
              const icon = typeof sl.data.icon === 'string' ? SLOT_ICONS[sl.data.icon]?.glyph : undefined;
              const talk = hasTalk(sl);
              return (
                <div key={sl.id} data-slot role="button" tabIndex={0} aria-pressed={selected === sl.id}
                  className={cx(s.slot, talk ? s.slotTalk : s.slotBlock, !!sl.data.auto && s.slotAuto, bad && s.slotBad, !!sl.data.proposal && s.slotProposal, selected === sl.id && s.slotOn)}
                  title={bad ?? (sl.data.proposal ? 'Предложение автосборки' : sl.data.auto ? 'Перерыв по правилу' : undefined)}
                  style={{ gridColumn: `${col + 1} / span 1`, top: (m - from) * PX, height: Math.max(ROW, duration * PX) - 2, ...(color ? { ['--edge' as string]: `var(--c-${color}-dot)` } : {}) }}
                  draggable onDragStart={(e) => { e.dataTransfer.setData('text/plain', sl.id); setDrag({ kind: 'slot', id: sl.id }); }} onDragEnd={() => setDrag(null)}
                  onClick={() => onSelect(sl.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(sl.id); } }}>
                  <div className={s.slotTime}>{String(sl.data.start)}–{hhmm(m + duration)} · {duration} мин{sl.data.locked ? <Icon name="lock" size={11} /> : null}</div>
                  <div className={s.slotTitle}>{icon ? <span className={s.glyph}>{icon}</span> : null}{String(sl.data.title ?? '')}</div>
                  {typeof sl.data.subtitle === 'string' && sl.data.subtitle ? <div className={s.slotSub}>{sl.data.subtitle}</div> : null}
                  {bad && <div className={s.slotBadText}>{bad}</div>}
                  <span className={s.stretch} aria-hidden="true" onPointerDown={(e) => startStretch(e, sl)} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
